import React, { useEffect, useState } from "react"

import { Modal } from "@mui/material"
import CircularProgress from "@mui/material/CircularProgress"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import FormLabel from "@mui/material/FormLabel"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Grid from "@mui/material/Grid2"
import TextField from "@mui/material/TextField"
import Chip from "@mui/material/Chip"
import { styled } from "@mui/system"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"


const FormGrid = styled(Grid)(() => ({
    display: "flex",
    flexDirection: "column",
}))


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Convert a Blob to the `\x`-prefixed hex string that PostgREST/Postgres
 * expects for `bytea` column values (public.email.attachment_content).
 */
async function blobToPgBytea(blob) {
    const buffer = await blob.arrayBuffer()
    const bytes = new Uint8Array(buffer)
    let hex = ""
    for (let i = 0; i < bytes.length; i++) {
        hex += bytes[i].toString(16).padStart(2, "0")
    }
    return `\\x${hex}`
}

const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return ""
    if (bytes < 1024) return `${bytes} o`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`
}

// Matches a plain address ("a@example.com") or a named address
// ("Name <a@example.com>") — the two formats the email provider accepts.
const PLAIN_EMAIL_REGEX = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/
const NAMED_EMAIL_REGEX = /^[^<>]+<[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+>$/
const isValidEmailEntry = (entry) => PLAIN_EMAIL_REGEX.test(entry) || NAMED_EMAIL_REGEX.test(entry)

/**
 * `to` may hold one or several comma-separated addresses. Returns an error
 * string to show the user, or "" if every entry is valid.
 */
function validateRecipients(value) {
    const entries = (value ?? "").split(",").map((e) => e.trim()).filter(Boolean)
    if (entries.length === 0) return "Veuillez indiquer au moins un destinataire."
    const invalid = entries.filter((e) => !isValidEmailEntry(e))
    if (invalid.length > 0) {
        return `Adresse${invalid.length > 1 ? "s" : ""} invalide${invalid.length > 1 ? "s" : ""} : ${invalid.join(", ")}`
    }
    return ""
}

// How long / how often we're willing to poll the `email` row for a verdict
// before giving up and telling the user to check back later. The row is
// normally picked up and processed within a couple of seconds, so this is a
// generous margin rather than an expected wait.
const STATUS_POLL_INTERVAL_MS = 2000
const STATUS_POLL_TIMEOUT_MS  = 30000

/**
 * Watches a freshly-inserted `email` row until the backend job that actually
 * sends it reports a verdict, then reports it via the callbacks:
 *   - `sent` flips to true              → onSent()
 *   - `error` becomes non-null          → onFailed(message)
 *   - neither happens within the budget → onFailed(message) with a
 *     "no confirmation yet" message (NOT necessarily a real failure — the
 *     job might just be slow — but this modal has no other way to reach the
 *     user later than the same onFailed channel).
 *
 * This runs detached from the component (fire-and-forget: it's kicked off
 * right before the modal closes) so the user isn't stuck watching a spinner
 * for up to 30s — they get on with their day and a snackbar reports the
 * real outcome whenever it arrives.
 */
async function watchEmailStatus(supabase, emailId, onSent, onFailed) {
    const startedAt = Date.now()

    while (Date.now() - startedAt < STATUS_POLL_TIMEOUT_MS) {
        let data, fetchError
        try {
            ;({ data, error: fetchError } = await supabase
                .from("email")
                .select("sent, error")
                .eq("id", emailId)
                .single())
        } catch (err) {
            fetchError = err
        }

        if (fetchError) {
            onFailed(`Impossible de vérifier le statut de l'envoi : ${fetchError.message}`)
            return
        }
        if (data.error) {
            onFailed(`L'envoi a échoué : ${data.error}`)
            return
        }
        if (data.sent) {
            onSent()
            return
        }

        await new Promise((resolve) => setTimeout(resolve, STATUS_POLL_INTERVAL_MS))
    }

    onFailed("L'email a bien été enregistré, mais nous n'avons pas reçu de confirmation d'envoi. Vérifiez son statut plus tard.")
}


// ---------------------------------------------------------------------------
// EmailModal
//
// Generic "compose & send" modal, shared by every email-sending flow in the
// app (rent reminders, rent receipts, and any future ones). Callers own the
// business logic (what to prefill, what template to use, what attachment to
// generate) — this component only knows how to preview those fields, persist
// an `email` row, and then watch that row until the backend actually
// confirms delivery (or failure).
//
// Props
//   supabase        – initialised Supabase client (needs INSERT *and* SELECT
//                      on `email` — SELECT is required to poll the row's
//                      `sent` / `error` columns after insert)
//   open / setOpen  – modal visibility
//   title           – modal heading, e.g. "Email de relance"
//   emailTo / emailSubject / emailMessage — prefilled values (all editable)
//   attachment      – optional { filename, blob }. When provided, a preview
//                      chip is shown and the file is attached to the email
//                      row (attachment_filename / attachment_content).
//   successMessage  – snackbar text reported to onSuccess once the backend
//                      confirms the email was actually sent (`sent = true`)
//   onSuccess(msg) / onError(msg) — called once a verdict is known. Note this
//                      can fire *after* the modal has already closed, since
//                      the modal closes as soon as the row is saved while the
//                      actual send is confirmed in the background.
// ---------------------------------------------------------------------------
const EmailModal = ({
                        supabase, open, setOpen,
                        title = "Email",
                        emailTo, emailSubject, emailMessage,
                        attachment = null,
                        successMessage = "L'email a bien été envoyé",
                        onSuccess, onError,
                    }) => {
    const [to,      setTo]      = useState(emailTo)
    const [subject, setSubject] = useState(emailSubject)
    const [message, setMessage] = useState(emailMessage)
    const [sending, setSending] = useState(false)
    const [toError, setToError] = useState("")

    useEffect(() => { setTo(emailTo); setToError("") }, [emailTo])
    useEffect(() => { setSubject(emailSubject) }, [emailSubject])
    useEffect(() => { setMessage(emailMessage) }, [emailMessage])

    // Object URL for the attachment preview — recreated whenever the
    // attachment changes, revoked on cleanup to avoid leaking memory.
    const [attachmentUrl, setAttachmentUrl] = useState(null)
    useEffect(() => {
        if (!attachment?.blob) {
            setAttachmentUrl(null)
            return
        }
        const url = URL.createObjectURL(attachment.blob)
        setAttachmentUrl(url)
        return () => URL.revokeObjectURL(url)
    }, [attachment])

    const handleClose = () => {
        setOpen(false)
        setTo(emailTo)
        setSubject(emailSubject)
        setMessage(emailMessage)
        setToError("")
    }

    const handleToChange = (e) => {
        setTo(e.target.value)
        // Clear a stale error as soon as the field looks valid again; don't
        // nag the user with a fresh error on every keystroke otherwise.
        if (toError) setToError(validateRecipients(e.target.value))
    }

    const handleSubmit = async () => {
        const validationError = validateRecipients(to)
        if (validationError) {
            setToError(validationError)
            return
        }

        setSending(true)
        try {
            const row = { recipient: to, subject, content: message }
            if (attachment?.blob) {
                row.attachment_filename = attachment.filename
                row.attachment_content = await blobToPgBytea(attachment.blob)
            }

            // `.select("id")` is required so we get the row back and can poll
            // it below — a plain `.insert(row)` returns no data.
            const { data: inserted, error: insertError } = await supabase
                .from("email")
                .insert(row)
                .select("id")
                .single()
            if (insertError) throw insertError

            // The row is saved and will be picked up by the backend job. We
            // don't block the UI on that job finishing — close the modal now
            // and let the watcher report the real outcome whenever it lands.
            watchEmailStatus(
                supabase,
                inserted.id,
                () => onSuccess(successMessage),
                (msg) => onError(msg)
            )
            handleClose()
        } catch (err) {
            onError(`Échec de l'enregistrement : ${err.message}`)
        } finally {
            setSending(false)
        }
    }

    return (
        <Modal open={open} onClose={handleClose} aria-labelledby="email-modal-title">
            <Card sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: "100%", md: "50%" }, boxShadow: 24, p: 4 }}>
                <CardContent>
                    <Typography id="email-modal-title" variant="h6" component="h2" sx={{ mb: 4 }}>
                        {title}
                    </Typography>
                    <Grid container spacing={2}>
                        <FormGrid item size={{ xs: 12 }}>
                            <FormLabel htmlFor="to">Destinataires</FormLabel>
                            <TextField
                                fullWidth id="to" value={to} onChange={handleToChange}
                                onBlur={() => setToError(validateRecipients(to))}
                                variant="outlined" size="small"
                                error={!!toError}
                                helperText={toError || " "}
                            />
                        </FormGrid>
                        <FormGrid item size={{ xs: 12 }}>
                            <FormLabel htmlFor="subject">Objet</FormLabel>
                            <TextField fullWidth id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} variant="outlined" size="small" />
                        </FormGrid>
                        <FormGrid item size={{ xs: 12 }}>
                            {/* TODO: use variant="outlined" but for now it's not working well with multiline */}
                            <FormLabel htmlFor="message">Message</FormLabel>
                            <TextField
                                fullWidth id="message" value={message} onChange={(e) => setMessage(e.target.value)}
                                variant="standard" size="small" multiline minRows={4} maxRows={10}
                                sx={{
                                    "& .MuiInputBase-root": { border: "1px solid rgba(0, 0, 0, 0.12)", borderRadius: "4px", padding: "10px", backgroundColor: "#fff" },
                                    "& .MuiInputBase-root.Mui-focused": { borderColor: "#1976d2" },
                                }}
                            />
                        </FormGrid>
                        {attachment && (
                            <FormGrid item size={{ xs: 12 }}>
                                <FormLabel>Pièce jointe</FormLabel>
                                <Stack direction="row">
                                    <Chip
                                        icon={<AttachFileIcon />}
                                        label={`${attachment.filename}${attachment.blob ? ` (${formatFileSize(attachment.blob.size)})` : ""}`}
                                        variant="outlined"
                                        onClick={() => attachmentUrl && window.open(attachmentUrl, "_blank")}
                                        deleteIcon={<OpenInNewIcon fontSize="small" />}
                                        onDelete={() => attachmentUrl && window.open(attachmentUrl, "_blank")}
                                        sx={{ maxWidth: "100%" }}
                                    />
                                </Stack>
                            </FormGrid>
                        )}
                        <FormGrid item size={{ xs: 6 }}>
                            <Button variant="contained" color="primary" fullWidth onClick={handleSubmit} disabled={sending}>
                                {sending ? <CircularProgress size={20} color="inherit" /> : "Envoyer"}
                            </Button>
                        </FormGrid>
                        <FormGrid item size={{ xs: 6 }}>
                            <Button variant="outlined" color="secondary" fullWidth onClick={handleClose} disabled={sending}>Annuler</Button>
                        </FormGrid>
                    </Grid>
                </CardContent>
            </Card>
        </Modal>
    )
}

export default EmailModal